import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import studentService from "../../services/studentService";
import { formatFileSize } from "../../utils/format";
import { useStudentAuth } from "../../context/StudentAuthContext";

const EMPTY = {
  university_name: "",
  name: "",
  student_id: "",
  email_id: "",
  education_qualification: "",
  percentage_10: "",
  year_10: "",
  percentage_12: "",
  year_12: "",
  current_cgpa: "",
  undergrad_cgpa: "",
  year_undergrad: "",
  specialisation: "",
  education_pass_yr: "",
    tenth_pass_year: "",
  twelfth_pass_year: "",
  undergrad_pass_year: "",
  has_work_experience: "",
  work_experience_years: "",
  work_experience_from_year: "",
  work_experience_to_year: "",
  work_experience_organizations: "",
};

const MAX_VIDEO_MB = 200;
const MAX_CV_MB = 15;

export default function ApplicationForm({ campuses = [], defaultCampus = "" }) {
  const { account, hasIntroVideo, refreshAccount } = useStudentAuth();
  const [form, setForm] = useState({ ...EMPTY, university_name: defaultCampus });
  const [cv, setCv] = useState(null);
  const [video, setVideo] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | success | error
  const [serverMessage, setServerMessage] = useState("");
  const cvInputRef = useRef();
  const videoInputRef = useRef();

  // Auto-fill from the logged-in account: name, college email, and
  // whichever campus the student clicked "Apply" from.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      university_name: defaultCampus || f.university_name,
      name: f.name || account?.full_name || "",
      email_id: f.email_id || account?.email || "",
    }));
  }, [defaultCampus, account]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const next = {};
    if (!form.university_name) next.university_name = "Select a campus.";
    if (!form.name.trim()) next.name = "Your name is required.";
    if (!form.student_id.trim()) next.student_id = "Student ID is required.";
    if (!form.email_id.trim() || !/^\S+@\S+\.\S+$/.test(form.email_id))
      next.email_id = "A valid college email is required.";
    if (!form.undergrad_cgpa.trim()) next.undergrad_cgpa = "Undergrad CGPA is required.";

    if (!form.has_work_experience) {
      next.has_work_experience = "Please select an option.";
    } else if (form.has_work_experience === "yes") {
      if (!form.work_experience_years) next.work_experience_years = "Number of years is required.";
      if (!form.work_experience_from_year) {next.work_experience_from_year = "From year is required.";}
      if (!form.work_experience_to_year) {next.work_experience_to_year = "To year is required.";}
      if (form.work_experience_from_year && form.work_experience_to_year && Number(form.work_experience_to_year) < Number(form.work_experience_from_year)) {next.work_experience_to_year = "To year cannot be before From year.";}
      if (!form.work_experience_organizations.trim()) next.work_experience_organizations = "Please list your organization(s).";
    }

    if (!cv) next.cv = "A CV is required to apply.";
    else if (cv.size > MAX_CV_MB * 1024 * 1024) next.cv = `CV must be under ${MAX_CV_MB}MB.`;

    if (!video && !hasIntroVideo) next.video = "An introduction video is required to apply.";
    else if (video && video.size > MAX_VIDEO_MB * 1024 * 1024) next.video = `Video must be under ${MAX_VIDEO_MB}MB.`;

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    setStatus("idle");
    setServerMessage("");

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (cv) fd.append("cv", cv);
    if (video) fd.append("video", video);

    try {
      await studentService.apply(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      await refreshAccount();
      setStatus("success");
      setForm({ ...EMPTY, university_name: defaultCampus, name: account?.full_name || "", email_id: account?.email || "" });
      setCv(null);
      setVideo(null);
      if (cvInputRef.current) cvInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";
    } catch (err) {
      setStatus("error");
      setServerMessage(err.friendlyMessage || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  }

  if (status === "success") {
    return (
      <motion.div
        className="pm-app-success"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="pm-app-success__check"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 260, damping: 16 }}
        >
          ✓
        </motion.div>
        <h3>Application Submitted Successfully</h3>
        <p>
          Thank you, {form.name || "candidate"}. Your application for{" "}
          <strong>{form.university_name || "the campus programme"}</strong> has been received.
          The Positive Moves team will reach out if you're shortlisted.
        </p>
        <button className="pm-btn pm-btn--outline" onClick={() => setStatus("idle")}>
          Submit another application
        </button>
      </motion.div>
    );
  }

  return (
    <form className="pm-app-form" onSubmit={handleSubmit} noValidate>
      <fieldset>
        <legend>Personal information</legend>
        <div className="pm-field">
          <label>Campus *</label>
          <select
            value={form.university_name}
            onChange={(e) => update("university_name", e.target.value)}
          >
            <option value="">Select a campus</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.university_name && <span className="pm-field__error">{errors.university_name}</span>}
        </div>
        <div className="pm-field-grid">
          <div className="pm-field">
            <label>Full name *</label>
            <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="As per your ID" />
            {errors.name && <span className="pm-field__error">{errors.name}</span>}
          </div>
          <div className="pm-field">
            <label>Student ID *</label>
            <input value={form.student_id} onChange={(e) => update("student_id", e.target.value)} placeholder="College roll / ID number" />
            {errors.student_id && <span className="pm-field__error">{errors.student_id}</span>}
          </div>
        </div>
        <div className="pm-field">
          <label>College email *</label>
          <input type="email" value={form.email_id} onChange={(e) => update("email_id", e.target.value)} placeholder="you@college.edu" />
          {errors.email_id && <span className="pm-field__error">{errors.email_id}</span>}
        </div>
      </fieldset>
      <fieldset>
        <legend>Academics</legend>

        {/* 10th */}
        <div className="pm-field-grid">
          <div className="pm-field">
            <label>10th standard % *</label>
            <input type="number" min="0" max="100" step="0.01" value={form.percentage_10} onChange={(e) => update("percentage_10", e.target.value)} placeholder="e.g. 84.5" />
          </div>

          <div className="pm-field">
            <label>10th Passing Year *</label>
            <input type="number" value={form.year_10} onChange={(e) => update("year_10", e.target.value)} placeholder="e.g. 2021" />
          </div>
        </div>

        {/* 12th */}
        <div className="pm-field-grid">
          <div className="pm-field">
            <label>12th standard % *</label>
            <input type="number" min="0" max="100" step="0.01" value={form.percentage_12} onChange={(e) => update("percentage_12", e.target.value)} placeholder="e.g. 86.5" />
          </div>

          <div className="pm-field">
            <label>12th Passing Year *</label>
            <input type="number" value={form.year_12} onChange={(e) => update("year_12", e.target.value)} placeholder="e.g. 2023" />
          </div>
        </div>

        {/* Undergraduation */}
        <div className="pm-field-grid">
          <div className="pm-field">
            <label>Undergraduation CGPA / % *</label>
            <input value={form.undergrad_cgpa} onChange={(e) => update("undergrad_cgpa", e.target.value)} placeholder="e.g. 7.9 or 79%" />
            {errors.undergrad_cgpa && (<span className="pm-field__error">{errors.undergrad_cgpa}</span>)}
          </div>

          <div className="pm-field">
            <label>Undergraduation Passing Year *</label>
            <input type="number" value={form.year_undergrad} onChange={(e) => update("year_undergrad", e.target.value)} placeholder="e.g. 2027" />
          </div>
        </div>

        {/* Current CGPA */}
        <div className="pm-field-grid">
          <div className="pm-field">
            <label>Current CGPA (if applicable)</label>
            <input value={form.current_cgpa} onChange={(e) => update("current_cgpa", e.target.value)} placeholder="e.g. 8.2" />
          </div>
          <div className="pm-field">
            <label>Passing Year</label>
            <input type="number" value={form.education_pass_yr} onChange={(e) => update("education_pass_yr", e.target.value)} placeholder="e.g. 2027" />
          </div>
        </div>

        <div className="pm-field-grid">
          <div className="pm-field">
            <label>Current Qualification</label>
            <input value={form.education_qualification} onChange={(e) => update("education_qualification", e.target.value)} placeholder="e.g. B.Tech, MBA" />
          </div>

          <div className="pm-field">
            <label>Specialisation</label>
            <input value={form.specialisation} onChange={(e) => update("specialisation", e.target.value)} placeholder="e.g. Finance, CSE" />
          </div>
        </div>
        <div className="pm-field">
          <label>Passing year</label>
          <input value={form.education_pass_yr} onChange={(e) => update("education_pass_yr", e.target.value)} placeholder="e.g. 2027" />
        </div>
                <div className="pm-field-grid">
          {[
            ["tenth_pass_year", "10th passing year"],
            ["twelfth_pass_year", "12th passing year"],
            ["undergrad_pass_year", "Undergraduate passing year"],
          ].map(([field, label]) => (
            <div className="pm-field" key={field}>
              <label htmlFor={field}>{label}</label>
              <input
                id={field}
                type="number"
                inputMode="numeric"
                min="1900"
                max={new Date().getFullYear() + 10}
                step="1"
                value={form[field]}
                onChange={(e) => update(field, e.target.value)}
                placeholder="YYYY"
              />
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Full-time work experience</legend>
        <div className="pm-field"><label>Do you have full-time work experience? *</label>
          <select value={form.has_work_experience} onChange={(e) => update("has_work_experience", e.target.value)}>
            <option value="">Select</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          {errors.has_work_experience && (<span className="pm-field__error"> {errors.has_work_experience}</span>)}
        </div>

        {form.has_work_experience === "yes" && (
          <>
            {/* Experience duration */}
            <div className="pm-field-grid-three">
              <div className="pm-field">
                <label>Total no. of years of work experience *</label>
                <input type="number" min="0" step="0.5" value={form.work_experience_years} onChange={(e) => update("work_experience_years", e.target.value)} placeholder="e.g. 1" />
                {errors.work_experience_years && (<span className="pm-field__error">{errors.work_experience_years}</span>)}
              </div>

              <div className="pm-field">
                <label>From Year *</label>
                <input type="number" min="2000" max="2100" value={form.work_experience_from_year} onChange={(e) => update("work_experience_from_year", e.target.value)} placeholder="e.g. 2024" />
                {errors.work_experience_from_year && (<span className="pm-field__error">{errors.work_experience_from_year}</span>)}
              </div>

              <div className="pm-field">
                <label>To Year *</label>
                <input type="number" min="1900" max="2100" value={form.work_experience_to_year} onChange={(e) =>update("work_experience_to_year", e.target.value)} placeholder="e.g. 2026" />
                {errors.work_experience_to_year && (<span className="pm-field__error">{errors.work_experience_to_year}</span>)}
              </div>
            </div>

            {/* Organization */}
            <div className="pm-field">
              <label>Organization Name *</label>
              <textarea rows={3} value={form.work_experience_organizations}
                onChange={(e) => update("work_experience_organizations", e.target.value)}
                placeholder="e.g. ABC Technologies Pvt. Ltd."
              />

              {errors.work_experience_organizations && (<span className="pm-field__error">{errors.work_experience_organizations}</span>)}
            </div>
          </>
        )}
      </fieldset>

      <fieldset>
        <legend>Documents</legend>
        <div className="pm-field-grid">
          <FileDrop
            label="CV / Resume *"
            accept=".pdf,.doc,.docx"
            hint="PDF, DOC or DOCX · up to 15MB"
            file={cv}
            inputRef={cvInputRef}
            onChange={setCv}
            error={errors.cv}
          />
          <FileDrop
            label={hasIntroVideo ? "Introduction video (already on file)" : "Introduction video *"}
            accept=".mp4,.mov,.webm,.mkv"
            hint={hasIntroVideo ? "On file — choose a file only to replace it" : "MP4, MOV, WEBM or MKV · up to 200MB"}
            file={video}
            inputRef={videoInputRef}
            onChange={setVideo}
            error={errors.video}
          />
        </div>
        {hasIntroVideo && !video && (
          <p className="pm-field__hint" style={{ marginTop: -8 }}>
            You've already uploaded an introduction video — it'll be reused for this application.
            Choose a new file above only if you'd like to replace it.
          </p>
        )}
      </fieldset>

      <AnimatePresence>
        {submitting && (
          <motion.div
            className="pm-progress"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="pm-progress__bar">
              <motion.div className="pm-progress__fill" animate={{ width: `${progress}%` }} />
            </div>
            <span>{progress < 100 ? `Uploading… ${progress}%` : "Finalising your application…"}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {status === "error" && (
        <motion.div className="pm-form-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {serverMessage}
        </motion.div>
      )}

      <motion.button
        type="submit"
        className="pm-btn pm-btn--primary pm-btn--lg"
        disabled={submitting}
        whileHover={{ scale: submitting ? 1 : 1.015 }}
        whileTap={{ scale: submitting ? 1 : 0.98 }}
      >
        {submitting ? "Submitting…" : "Submit application"}
      </motion.button>
    </form>
  );
}

function FileDrop({ label, accept, hint, file, inputRef, onChange, error }) {
  return (
    <div className="pm-field">
      <label>{label}</label>
      <div className="pm-filedrop" onClick={() => inputRef.current?.click()}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
        {file ? (
          <div className="pm-filedrop__file">
            <span className="pm-filedrop__name">{file.name}</span>
            <span className="pm-filedrop__size">{formatFileSize(file.size)}</span>
          </div>
        ) : (
          <span className="pm-filedrop__placeholder">Click to choose a file · {hint}</span>
        )}
      </div>
      {error && <span className="pm-field__error">{error}</span>}
    </div>
  );
}
